import { useState, useEffect } from 'react';
import { getAvatarUrl } from '../../utils/avatar';
import { Users, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

export default function LecturerStudents() {
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedGrade, setSelectedGrade] = useState('');

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const res = await api.get('/lecturer/students');
      if (res.data.success && res.data.data) {
        setStudents(res.data.data.students || []);
      }
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  const grades = [...new Set(students.map(s => s.grade_name))];
  
  const filteredStudents = students.filter(s => {
    const matchesSearch = s.full_name?.toLowerCase().includes(search.toLowerCase()) || 
                          s.email?.toLowerCase().includes(search.toLowerCase());
    const matchesGrade = !selectedGrade || s.grade_name === selectedGrade;
    return matchesSearch && matchesGrade;
  });

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div></div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-800">Students</h1>
        <p className="text-gray-500">Students in your assigned grades</p>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        <div className="p-4 border-b border-gray-100 flex items-center justify-between flex-wrap gap-4">
          <h3 className="font-semibold text-gray-800">Students ({filteredStudents.length})</h3>
          <div className="flex gap-3">
            <select value={selectedGrade} onChange={(e) => setSelectedGrade(e.target.value)}
              className="px-3 py-2 bg-gray-50 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500">
              <option value="">All Grades</option>
              {grades.map(g => <option key={g} value={g}>{g}</option>)}
            </select>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input type="text" placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)}
                className="pl-10 pr-4 py-2 bg-gray-50 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 w-64" />
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Student</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Email</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Student ID</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Grade</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredStudents.map((student) => (
                <tr key={student.user_id} className="hover:bg-indigo-50 cursor-pointer transition" onClick={() => navigate(`/lecturer/students/${student.user_id}`)}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center overflow-hidden">
                        { (student.avatar_thumb_path || student.avatar_path) ? (
                          <img src={getAvatarUrl(student.avatar_thumb_path || student.avatar_path)} alt="avatar" className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-indigo-600 font-medium">{student.full_name?.charAt(0)}</span>
                        ) }
                      </div>
                      <span className="font-medium text-gray-800">{student.full_name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-600">{student.email}</td>
                  <td className="px-4 py-3 text-gray-600">{student.student_id_number || '-'}</td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-1 bg-blue-50 text-blue-600 rounded-full text-xs font-medium">{student.grade_name}</span>
                  </td>
                </tr>
              ))}
              {filteredStudents.length === 0 && (
                <tr><td colSpan="4" className="px-4 py-8 text-center text-gray-400">No students found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
